// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '@loa/ui-components';
import { clientApi } from '../../../api/clientApi';
import { relationshipApi } from '../../../api/relationshipApi';
import { proposalApi } from '../../../api/proposalApi';
import type { Client } from '../../../types/client';
import type { Quote } from '../../../types/quote';
import type { PensionWizardBootstrapResult, Proposal } from '../../../types/proposal';
import { usePageHeader } from '../../../contexts/PageHeaderContext';
import { WizardStepQuoteRequest } from './WizardStepQuoteRequest';
import { WizardStepQuoteOptions } from './WizardStepQuoteOptions';
import { WizardStepSosBuilder } from './WizardStepSosBuilder';
import { WizardStepReviewSend } from './WizardStepReviewSend';
import './PensionProposalWizard.css';

type WizardStep = 1 | 2 | 3 | 4;

const STEPPER_ITEMS: ReadonlyArray<{ step: WizardStep; label: string }> = [
  { step: 1, label: 'Quote request' },
  { step: 2, label: 'Quote options' },
  { step: 3, label: 'Statement of Suitability' },
  { step: 4, label: 'Review & send' },
];

/**
 * Builder-v2 brokerages leave the wizard after step 2 ("Create Statement of
 * Suitability" lands on the sectioned builder page), so the stepper shows the
 * handover instead of v1's in-wizard steps 3-4.
 */
const BUILDER_STEPPER_ITEMS: ReadonlyArray<{ step: WizardStep; label: string }> = [
  { step: 1, label: 'Quote request' },
  { step: 2, label: 'Quote options' },
  { step: 3, label: 'Proposal Builder' },
];

/**
 * Advisor UI shell for the four-step pension proposal wizard
 * (docs/specs/pension-proposal-wizard-prd.md §3.1). Phase 1 wired steps 1–2
 * end to end (quote request → quote options); Phase 2 adds Step 3 (the
 * Statement of Suitability builder) and Step 4 (review & send) — all four
 * steps are resumable via the server-derived `wizardStep`.
 *
 * Route: `/relationships/:id/proposals/pension-wizard/:proposalId?`. With no
 * `proposalId` the wizard starts fresh at Step 1; with a `proposalId` it
 * resumes at the server-derived `wizardStep` (`GET /api/proposals/:id`).
 */
export function PensionProposalWizard() {
  const { id: profileId, proposalId: proposalIdParam } = useParams<{
    id: string;
    proposalId?: string;
  }>();
  const navigate = useNavigate();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();

  const [clients, setClients] = useState<Client[]>([]);
  const [brokerageId, setBrokerageId] = useState('');
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [step, setStep] = useState<WizardStep>(proposalIdParam ? 2 : 1);
  const [editQuote, setEditQuote] = useState<Quote | null>(null);
  const [optionsRefreshKey, setOptionsRefreshKey] = useState(0);
  const [loading, setLoading] = useState(Boolean(proposalIdParam));
  const [error, setError] = useState<string | null>(null);
  const [builderEnabled, setBuilderEnabled] = useState(false);

  // Ask the backend once per mount whether the sectioned Pension Proposal
  // Builder v2 is active for this brokerage; default to false (v1 Step 3
  // SoS editor) on any failure or when the endpoint isn't available yet —
  // mirrors ProposalsTab's pensionWizardEnabled probe.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => proposalApi.pensionBuilderEnabled?.())
      .then((enabled) => {
        if (!cancelled) setBuilderEnabled(Boolean(enabled));
      })
      .catch(() => {
        if (!cancelled) setBuilderEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Builder-enabled brokerages never start a NEW proposal on wizard v1 — the
  // sectioned builder hosts Quote Request/Quote Options as its first two rail
  // sections, so new-proposal traffic lands there (legacy links included).
  useEffect(() => {
    if (builderEnabled && !proposalIdParam && profileId) {
      navigate(`/relationships/${profileId}/proposals/pension-builder`, { replace: true });
    }
  }, [builderEnabled, proposalIdParam, profileId, navigate]);

  // Relationship (profile) display name for the breadcrumb trail.
  const [relationshipName, setRelationshipName] = useState<string | null>(null);

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    relationshipApi
      .getRelationship(profileId)
      .then((r) => {
        if (!cancelled) setRelationshipName(r.relationshipName || r.profileName || null);
      })
      .catch(() => {
        if (!cancelled) setRelationshipName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Relationships', to: '/relationships' },
      ...(profileId && relationshipName
        ? [{ label: relationshipName, to: `/relationships/${profileId}` }]
        : []),
      {
        label: 'Proposals',
        to: profileId ? `/relationships/${profileId}?tab=proposals` : undefined,
      },
    ]);
    setBackTo(profileId ? `/relationships/${profileId}?tab=proposals` : null);
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [profileId, relationshipName, setBreadcrumbItems, setBackTo]);

  // Clients + brokerageId are needed regardless of resume/create mode.
  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    clientApi
      .getClientsByProfile(profileId)
      .then((list) => {
        if (!cancelled) setClients(list);
      })
      .catch(() => {
        // Non-fatal — the client radio just renders empty and the advisor
        // can retry by reloading.
      });
    relationshipApi
      .getRelationship(profileId)
      .then((rel) => {
        if (!cancelled) setBrokerageId(rel.brokerageId);
      })
      .catch(() => {
        // Non-fatal — provider fetch in Step 1 simply returns no options.
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  // Resume: load the proposal and derive the step to land on.
  useEffect(() => {
    if (!proposalIdParam) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    proposalApi
      .getProposal(proposalIdParam)
      .then((p) => {
        if (cancelled) return;
        // Builder-born proposals (sectioned builder v2) never resume into
        // wizard v1 at any step — the builder hosts the quote steps as its
        // first two rail sections and itself picks the right one to land on.
        if (p.builder) {
          navigate(`/relationships/${profileId}/proposals/pension-builder/${p.id}`, {
            replace: true,
          });
          return;
        }
        setProposal(p);
        if (p.wizardStep === 4) setStep(4);
        else if (p.wizardStep === 3) setStep(3);
        else setStep(2);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load this proposal');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [proposalIdParam, navigate, profileId]);

  const handleBootstrapped = (result: PensionWizardBootstrapResult) => {
    setProposal(result.proposal);
    setStep(2);
    navigate(`/relationships/${profileId}/proposals/pension-wizard/${result.proposalId}`, {
      replace: true,
    });
  };

  const handleEditRequested = (quote: Quote) => {
    setEditQuote(quote);
    setStep(1);
  };

  const handleQuoteUpdated = () => {
    setEditQuote(null);
    setOptionsRefreshKey((k) => k + 1);
    setStep(2);
  };

  const handleCancelStep1 = () => {
    if (editQuote) {
      // Cancelling an edit just returns to the options screen unchanged.
      setEditQuote(null);
      setStep(2);
      return;
    }
    navigate(`/relationships/${profileId}?tab=proposals`);
  };

  const handleSaveAndClose = () => {
    navigate(`/relationships/${profileId}?tab=proposals`);
  };

  const handleCreateStatement = () => {
    // Builder-enabled brokerages skip wizard v1's step 3/4 entirely and land
    // on the sectioned builder page (PRD §4 A1/A2); flag off keeps the v1
    // in-wizard SoS editor untouched.
    if (builderEnabled && proposal) {
      navigate(`/relationships/${profileId}/proposals/pension-builder/${proposal.id}`);
      return;
    }
    setStep(3);
  };

  const handleBackToQuoteOptions = () => {
    setStep(2);
  };

  const handleSosGenerated = () => {
    setStep(4);
  };

  const handleBackToSosBuilder = () => {
    setStep(3);
  };

  const handleSent = () => {
    navigate(`/relationships/${profileId}?tab=proposals`);
  };

  if (!profileId) return null;

  if (loading) {
    return (
      <div className="pension-wizard" data-testid="pension-wizard-loading">
        <div className="pension-wizard__card" style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pension-wizard">
        <div className="pension-wizard__card pension-wizard__error" data-testid="pension-wizard-error">
          {error}
        </div>
      </div>
    );
  }

  const quoteId = proposal?.sourceQuoteId;

  return (
    <div className="pension-wizard" data-testid="pension-wizard">
      <nav className="pension-wizard__stepper" aria-label="Pension proposal wizard progress">
        {(builderEnabled ? BUILDER_STEPPER_ITEMS : STEPPER_ITEMS).map((item, index) => (
          <span key={item.step} className="pension-wizard__stepper-item">
            {index > 0 && <span className="pension-wizard__stepper-divider">·</span>}
            <span
              className={
                item.step === step
                  ? 'pension-wizard__stepper-label pension-wizard__stepper-label--active'
                  : 'pension-wizard__stepper-label'
              }
            >
              {item.step} {item.label}
            </span>
          </span>
        ))}
      </nav>

      <div className="pension-wizard__card">
        {step === 1 && (
          <WizardStepQuoteRequest
            profileId={profileId}
            brokerageId={brokerageId}
            clients={clients}
            existingQuote={editQuote ?? undefined}
            targetClientId={proposal?.targetClientId}
            onBootstrapped={handleBootstrapped}
            onQuoteUpdated={handleQuoteUpdated}
            onCancel={handleCancelStep1}
          />
        )}

        {step === 2 && quoteId && (
          <WizardStepQuoteOptions
            key={`${quoteId}-${optionsRefreshKey}`}
            quoteId={quoteId}
            onEditRequested={handleEditRequested}
            onSaveAndClose={handleSaveAndClose}
            onCreateStatement={handleCreateStatement}
          />
        )}

        {step === 3 && (
          <WizardStepSosBuilder
            proposalId={proposalIdParam ?? proposal?.id ?? ''}
            profileId={profileId}
            onBack={handleBackToQuoteOptions}
            onGenerated={handleSosGenerated}
          />
        )}

        {step === 4 && proposal && (
          <WizardStepReviewSend
            proposalId={proposalIdParam ?? proposal.id}
            proposal={proposal}
            onBack={handleBackToSosBuilder}
            onSent={handleSent}
          />
        )}
      </div>
    </div>
  );
}

export default PensionProposalWizard;
